import { useState, useRef, useEffect } from "react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, Table2, Type, Trash2, ChevronUp, ChevronDown, MoreHorizontal } from "lucide-react";

const COLORS = { none: "", red: "#A83232", green: "#5f6b4a", blue: "#2f5d8a", amber: "#B0822A" };
const COLOR_LABEL = { none: "Ingen markering", red: "Rød", green: "Grøn", blue: "Blå", amber: "Gul" };
const SWATCHES = ["none", "red", "green", "blue", "amber"];
const nid = () => Math.random().toString(36).slice(2, 10);

const asList = (blocks, minute) => {
  if (blocks && blocks.length) return blocks;
  if (minute) return [{ id: nid(), type: "text", text: minute }];
  return [];
};

// ------------------------------------------------------------- READ-ONLY VIEW
export function MinuteTableView({ table, compact }) {
  const headers = table?.headers || [];
  const rows = table?.rows || [];
  const colors = table?.colors || {};
  return (
    <div className="overflow-x-auto my-2" data-testid="minute-table-view">
      <table className="w-full border-collapse text-sm">
        {headers.length > 0 && (
          <thead><tr>
            {headers.map((h, c) => (
              <th key={c} className="text-left font-semibold px-3 py-2 border-b-2 border-[#EBE8E3]" style={{ color: "#1A1919" }}>{h}</th>
            ))}
          </tr></thead>
        )}
        <tbody>
          {rows.map((row, r) => (
            <tr key={r} className="align-top">
              {row.map((cell, c) => (
                <td key={c} className={`px-3 ${compact ? "py-1.5" : "py-2.5"} border-b border-[#F0EDE8] whitespace-pre-wrap`}
                    style={{ color: COLORS[colors[`${r}-${c}`]] || "#44403C", fontWeight: colors[`${r}-${c}`] && colors[`${r}-${c}`] !== "none" ? 600 : 400 }}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function MinuteBlocksView({ blocks, minute, empty = "—", textClass = "text-sm text-stone-700", tableCompact }) {
  const list = asList(blocks, minute);
  if (!list.length) return <p className={`${textClass} italic text-stone-400`}>{empty}</p>;
  return (
    <div className="space-y-2" data-testid="minute-blocks-view">
      {list.map((b) => b.type === "table"
        ? <MinuteTableView key={b.id} table={b} compact={tableCompact} />
        : (b.text ? <p key={b.id} className={`${textClass} whitespace-pre-wrap leading-relaxed`}>{b.text}</p> : null))}
    </div>
  );
}

// ------------------------------------------------------------- EDITOR
const menuItem = "text-sm cursor-pointer";

function Dots({ items, testid, className = "" }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className={`h-7 w-7 rounded-md inline-flex items-center justify-center text-stone-400 hover:text-stone-800 hover:bg-[#F3F1EC] ${className}`} data-testid={testid} aria-label="Menu"><MoreHorizontal className="h-4 w-4" /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-xl min-w-[9rem]">
        {items.filter(Boolean).map(([label, fn, cls, tid]) => <DropdownMenuItem key={label} onClick={fn} className={`${menuItem} ${cls || ""}`} data-testid={tid}>{label}</DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function AutoTextarea({ value, onChange, className = "", testid, placeholder, rows = 1 }) {
  const ref = useRef(null);
  useEffect(() => { const el = ref.current; if (!el) return; el.style.height = "0px"; el.style.height = `${Math.max(el.scrollHeight, 28)}px`; }, [value]);
  return <textarea ref={ref} rows={rows} value={value} onChange={onChange} placeholder={placeholder} data-testid={testid}
    className={`w-full resize-none bg-transparent outline-none rounded-md border border-transparent px-1.5 py-1 -mx-1.5 focus:border-[#D6D3D1] focus:bg-white focus:ring-2 focus:ring-[#EBE8E3] transition-colors ${className}`} />;
}

function CellMenu({ value, onPick, onDelRow, onDelCol, testid }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="absolute top-1 right-0.5 h-5 w-5 rounded inline-flex items-center justify-center text-stone-300 opacity-0 group-hover/cell:opacity-100 focus:opacity-100 hover:text-stone-700 hover:bg-[#F3F1EC]" data-testid={testid} aria-label="Cellemenu"><MoreHorizontal className="h-3.5 w-3.5" /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-xl min-w-[10rem]">
        {SWATCHES.map((s) => <DropdownMenuItem key={s} onClick={() => onPick(s)} className={`${menuItem} gap-2 ${value === s ? "font-semibold" : ""}`}><span className="h-3 w-3 rounded-full border border-[#D6D3D1]" style={{ background: s === "none" ? "#fff" : COLORS[s] }} />{COLOR_LABEL[s]}</DropdownMenuItem>)}
        <DropdownMenuItem onClick={onDelRow} className={`${menuItem} text-[#A83232] border-t mt-1 pt-1.5`}>Slet række</DropdownMenuItem>
        {onDelCol && <DropdownMenuItem onClick={onDelCol} className={`${menuItem} text-[#A83232]`}>Slet kolonne</DropdownMenuItem>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function TableEditor({ block, onChange, testid }) {
  const headers = block.headers || [];
  const rows = block.rows || [];
  const colors = block.colors || {};
  const set = (patch) => onChange({ ...block, ...patch });
  const setHeader = (c, v) => set({ headers: headers.map((h, i) => (i === c ? v : h)) });
  const setCell = (r, c, v) => set({ rows: rows.map((row, ri) => ri === r ? row.map((cell, ci) => ci === c ? v : cell) : row) });
  const setColor = (r, c, col) => { const nc = { ...colors }; if (!col || col === "none") delete nc[`${r}-${c}`]; else nc[`${r}-${c}`] = col; set({ colors: nc }); };
  const addRow = () => set({ rows: [...rows, headers.map(() => "")] });
  const delRow = (r) => set({ rows: rows.filter((_, i) => i !== r), colors: remapDrop(colors, r, "r") });
  const addCol = () => set({ headers: [...headers, ""], rows: rows.map((row) => [...row, ""]) });
  const delCol = (c) => set({ headers: headers.filter((_, i) => i !== c), rows: rows.map((row) => row.filter((_, i) => i !== c)), colors: remapDrop(colors, c, "c") });

  return (
    <div className="group/table relative" data-testid={testid}>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead><tr>
            {headers.map((h, c) => (
              <th key={c} className="text-left px-1 py-1 border-b-2 border-[#EBE8E3] min-w-[120px]">
                <input value={h} onChange={(e) => setHeader(c, e.target.value)} className="w-full bg-transparent outline-none font-semibold text-[#1A1919] px-1.5 py-1 rounded-md border border-transparent focus:border-[#D6D3D1] focus:bg-white" placeholder={`Kolonne ${c + 1}`} data-testid={`${testid}-h-${c}`} />
              </th>
            ))}
          </tr></thead>
          <tbody>
            {rows.map((row, r) => (
              <tr key={r}>
                {row.map((cell, c) => {
                  const col = colors[`${r}-${c}`];
                  return (
                    <td key={c} className="group/cell relative px-1 py-0.5 align-top border-b border-[#F0EDE8]" style={col && col !== "none" ? { background: `${COLORS[col]}14` } : undefined}>
                      {col && col !== "none" && <span className="absolute left-1.5 top-2.5 h-2 w-2 rounded-full" style={{ background: COLORS[col] }} />}
                      <AutoTextarea value={cell} onChange={(e) => setCell(r, c, e.target.value)} testid={`${testid}-cell-${r}-${c}`} className={`text-sm pr-5 ${col && col !== "none" ? "font-semibold pl-4" : ""}`} />
                      <CellMenu value={col || "none"} onPick={(v) => setColor(r, c, v)} onDelRow={() => delRow(r)} onDelCol={headers.length > 1 ? () => delCol(c) : null} testid={`${testid}-cellmenu-${r}-${c}`} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex gap-3 mt-1.5 text-xs text-stone-500 opacity-0 group-hover/table:opacity-100 focus-within:opacity-100 transition-opacity">
        <button type="button" onClick={addRow} className="hover:text-stone-900 flex items-center gap-1" data-testid={`${testid}-addrow`}><Plus className="h-3.5 w-3.5" /> Række</button>
        <button type="button" onClick={addCol} className="hover:text-stone-900 flex items-center gap-1" data-testid={`${testid}-addcol`}><Plus className="h-3.5 w-3.5" /> Kolonne</button>
      </div>
    </div>
  );
}

function remapDrop(colors, idx, axis) {
  const out = {};
  Object.entries(colors).forEach(([k, v]) => {
    const [r, c] = k.split("-").map(Number);
    const t = axis === "r" ? r : c;
    if (t === idx) return;
    const nr = axis === "r" && r > idx ? r - 1 : r;
    const nc = axis === "c" && c > idx ? c - 1 : c;
    out[`${nr}-${nc}`] = v;
  });
  return out;
}

function AddBetween({ onText, onTable, always, testid }) {
  return (
    <div className={`flex items-center gap-2 h-6 ${always ? "" : "opacity-0 hover:opacity-100 focus-within:opacity-100"} transition-opacity`} data-testid={testid}>
      <div className="flex-1 h-px bg-[#EBE8E3]" />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className="h-6 w-6 rounded-full border border-[#D6D3D1] bg-white text-stone-500 hover:text-stone-900 hover:border-stone-500 inline-flex items-center justify-center" data-testid={`${testid}-btn`} aria-label="Tilføj blok"><Plus className="h-3.5 w-3.5" /></button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="rounded-xl">
          <DropdownMenuItem onClick={onText} className={`${menuItem} gap-2`} data-testid={`${testid}-text`}><Type className="h-4 w-4" /> Tekst</DropdownMenuItem>
          <DropdownMenuItem onClick={onTable} className={`${menuItem} gap-2`} data-testid={`${testid}-table`}><Table2 className="h-4 w-4" /> Tabel</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <div className="flex-1 h-px bg-[#EBE8E3]" />
    </div>
  );
}

export function MinuteBlocksEditor({ blocks, minute, onChange, testid = "le-minute", textClass = "text-[17px] leading-relaxed text-[#1A1919]" }) {
  const [list, setList] = useState(() => { const l = asList(blocks, minute); return l.length ? l : [{ id: nid(), type: "text", text: "" }]; });
  const timer = useRef();
  const commit = (next) => {
    setList(next);
    clearTimeout(timer.current);
    const text = next.filter((b) => b.type === "text").map((b) => b.text || "").join("\n\n").trim();
    timer.current = setTimeout(() => onChange(next, text), 500);
  };
  const update = (id, patch) => commit(list.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  const remove = (id) => { const next = list.filter((b) => b.id !== id); commit(next.length ? next : [{ id: nid(), type: "text", text: "" }]); };
  const moveBlk = (i, dir) => { const j = i + dir; if (j < 0 || j >= list.length) return; const cp = [...list]; [cp[i], cp[j]] = [cp[j], cp[i]]; commit(cp); };
  const newText = () => ({ id: nid(), type: "text", text: "" });
  const newTable = () => ({ id: nid(), type: "table", headers: ["Punkt", "Status", "Handling"], rows: [["", "", ""], ["", "", ""]], colors: {} });
  const insertAt = (i, blk) => { const cp = [...list]; cp.splice(i, 0, blk); commit(cp); };

  return (
    <div data-testid={`${testid}-editor`}>
      {list.map((b, i) => (
        <div key={b.id}>
          <div className="group/blk relative" data-testid={`${testid}-block-${i}`}>
            {b.type === "text" ? (
              <AutoTextarea value={b.text || ""} onChange={(e) => update(b.id, { text: e.target.value })} rows={2} className={textClass} placeholder="Skriv referat og beslutninger…" testid={`${testid}-text-${i}`} />
            ) : (
              <TableEditor block={b} onChange={(nb) => update(b.id, nb)} testid={`${testid}-table-${i}`} />
            )}
            <Dots className="absolute -right-8 top-0.5 opacity-0 group-hover/blk:opacity-100 focus:opacity-100 data-[state=open]:opacity-100 max-md:opacity-100 max-md:-right-1" testid={`${testid}-menu-${i}`}
              items={[i > 0 && ["Flyt op", () => moveBlk(i, -1), "", `${testid}-up-${i}`], i < list.length - 1 && ["Flyt ned", () => moveBlk(i, 1), "", `${testid}-down-${i}`], ["Slet", () => remove(b.id), "text-[#A83232]", `${testid}-del-${i}`]]} />
          </div>
          <AddBetween always={i === list.length - 1} onText={() => insertAt(i + 1, newText())} onTable={() => insertAt(i + 1, newTable())} testid={`${testid}-add-${i}`} />
        </div>
      ))}
    </div>
  );
}
